import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CHANNEL, TEMPLATE_TYPE } from '../../../../constants/messageTemplateKeys';
import {
  selectTemplatesCampaigns,
  selectTemplatesTags,
  selectTemplatesTriggers,
  selectTemplatesTenants,
  selectTemplatesTenantAdditionalLanguages,
} from '../../../../store/templatesSlice';
import { buildBasicInformationErrors } from './buildBasicInformationErrors';
import {
  buildLanguageOptions,
  isLanguageEditable,
  normalizeLanguage,
  tenantHasLanguageChoice,
} from '../../../../lib/languageHelpers';
import { isLiveTemplateStatus } from '../../../../lib/isLiveTemplateStatus';
import { mapNamedIdOptions } from './mapNamedIdOptions';
import { useObservedElementWidth } from './useObservedElementWidth';
import {
  addSubject,
  buildSubjectErrors,
  canAddSubject,
  removeSubjectAt,
  setSubjectAt,
} from './subjectHelpers';
import {
  addSendCondition,
  canAddSendCondition,
  computeDayOptionalByRow,
  getInitialSendConditions,
  isTriggerSectionRequired,
  makeSendCondition,
  removeSendConditionAt,
  setSendConditionAt,
} from './sendConditionHelpers';
import { useTriggerValidation } from './useTriggerValidation';
import { buildSendConditionRowErrors } from './buildSendConditionRowErrors';
import { useTouchedFields } from './useTouchedFields';
import { ERROR_REQUIRED } from '../../../../../../shared/constants/validationErrors';

function getInitialType(initialValues) {
  return String(initialValues?.type ?? TEMPLATE_TYPE.MARKETING) === TEMPLATE_TYPE.TRIGGER
    ? TEMPLATE_TYPE.TRIGGER
    : TEMPLATE_TYPE.MARKETING;
}

function getInitialSubjects(initialValues) {
  if (Array.isArray(initialValues?.subjects) && initialValues.subjects.length > 0) {
    return initialValues.subjects.map((subject) => String(subject ?? ''));
  }
  return [''];
}

function getInitialIds(values) {
  return Array.isArray(values)
    ? values.map((id) => String(id ?? '')).filter((id) => id.length > 0)
    : [];
}

// "Select all" row value — a UI affordance; never stored as a selected value (so no pill).
export const SELECT_ALL_OPTION_ID = 'all';

function getInitialTenantIds(initialValues, { isAdmin, ownTenantId }) {
  const provided = getInitialIds(initialValues?.selectedTenantIds);
  if (provided.length > 0) return provided;
  // Default selection is the admin's own tenant.
  if (isAdmin && ownTenantId) return [String(ownTenantId)];
  return [];
}

export function useBasicInformationForm({
  channel = CHANNEL.EMAIL,
  saveAttempt = 0,
  revealNonce = 0,
  onFormChange,
  onTouchedChange,
  initialValues,
  requireTemplateName = false,
  hasTriedSave = false,
  templateId = null,
  isAdmin = false,
  ownTenantId = null,
  isEditMode = false,
  currentStatus = null,
}) {
  const [templateName, setTemplateName] = useState(() => String(initialValues?.templateName ?? ''));
  const [type, setType] = useState(() => getInitialType(initialValues));
  const [language, setLanguage] = useState(() => normalizeLanguage(initialValues?.language));
  const [subjects, setSubjects] = useState(() => getInitialSubjects(initialValues));
  const [selectedCampaignIds, setSelectedCampaignIds] = useState(() =>
    getInitialIds(initialValues?.selectedCampaignIds),
  );
  const [selectedTagIds, setSelectedTagIds] = useState(() =>
    getInitialIds(initialValues?.selectedTagIds),
  );
  const [selectedTenantIds, setSelectedTenantIds] = useState(() =>
    getInitialTenantIds(initialValues, { isAdmin, ownTenantId }),
  );
  const [triggerId, setTriggerId] = useState(() => String(initialValues?.triggerId ?? ''));
  const [sendConditions, setSendConditions] = useState(() =>
    getInitialSendConditions(initialValues?.sendConditions),
  );
  // The row the user last edited — a duplicate message is shown on it rather than on
  // whichever row the backend flagged.
  const [lastEditedSendConditionIndex, setLastEditedSendConditionIndex] = useState(-1);

  // Display-only "touched" tracking (see useTouchedFields): a required error shows for a
  // field/row only once it is touched. Initial counts match the form lists above, so the
  // touched arrays start in lockstep with them.
  const touchedFields = useTouchedFields({
    subjectCount: subjects.length,
    sendConditionCount: sendConditions.length,
  });

  const campaignWidthProbeRef = useRef(null);
  const tagsMenuWidthPx = useObservedElementWidth(campaignWidthProbeRef);

  const campaigns = useSelector(selectTemplatesCampaigns);
  const tags = useSelector(selectTemplatesTags);
  const triggers = useSelector(selectTemplatesTriggers);
  const tenants = useSelector(selectTemplatesTenants);
  const tenantAdditionalLanguages = useSelector(selectTemplatesTenantAdditionalLanguages);

  const isEmail = channel === CHANNEL.EMAIL;
  const isMarketing = type === TEMPLATE_TYPE.MARKETING;
  // Derived flag: every existing tenant is selected (the select-all sentinel is never stored).
  const allTenantIds = useMemo(() => (tenants ?? []).map((tenant) => String(tenant.id)), [tenants]);
  const isForAllTenants =
    allTenantIds.length > 0 && allTenantIds.every((id) => selectedTenantIds.includes(id));

  // Default to the admin's own tenant once it appears in the loaded list — once, and only while
  // the field is empty (a manual selection is never overwritten; skipped in edit/clone or non-admin).
  const tenantDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (tenantDefaultAppliedRef.current) return;
    if (!isAdmin || !ownTenantId) return;
    if (getInitialIds(initialValues?.selectedTenantIds).length > 0) return;
    const ownTenantIdStr = String(ownTenantId);
    const matchesOwnTenant = (tenants ?? []).some((tenant) => String(tenant.id) === ownTenantIdStr);
    if (!matchesOwnTenant) return;
    tenantDefaultAppliedRef.current = true;
    setSelectedTenantIds((prev) => (prev.length > 0 ? prev : [ownTenantIdStr]));
  }, [isAdmin, ownTenantId, tenants, initialValues]);

  // On each save/publish attempt, snapshot which required-relevant fields *currently fail*, so
  // exactly those errors become visible. Reading the values as of the attempt (not as deps) is
  // intentional: a row added or a field cleared after the attempt must stay clean until the
  // next attempt — that's what stops a *new* error from appearing mid-edit. An already-revealed
  // error still clears live once the field is fixed (the per-field error condition goes false).
  useEffect(() => {
    if (revealNonce === 0) return;
    const isEmpty = (value) => String(value ?? '').trim().length === 0;
    const hasCampaign =
      Array.isArray(selectedCampaignIds) &&
      selectedCampaignIds.some((id) => String(id ?? '').trim().length > 0);
    touchedFields.markVisible({
      scalar: {
        templateName: isEmpty(templateName),
        campaign: isMarketing && !hasCampaign,
        trigger: !isMarketing && isEmpty(triggerId),
        tenant: !isEditMode && isAdmin && selectedTenantIds.length === 0,
      },
      subjects: subjects.map((subject) => isEmail && isEmpty(subject)),
      sendConditions: sendConditions.map((row) => ({
        sendCondition: !isMarketing && isEmpty(row?.sendCondition),
        day: !isMarketing && isEmpty(row?.day),
      })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealNonce]);

  // Per-row "Days may be empty" flags (admins, or untouched migrated rows in edit mode).
  const dayOptionalByRow = useMemo(
    () =>
      computeDayOptionalByRow({
        isAdmin,
        isEditMode,
        triggerId,
        sendConditions,
        initialTriggerId: initialValues?.triggerId,
        initialSendConditions: initialValues?.sendConditions,
      }),
    [isAdmin, isEditMode, triggerId, sendConditions, initialValues],
  );

  const errors = useMemo(
    () =>
      buildBasicInformationErrors({
        channel,
        saveAttempt,
        templateName,
        subjects,
        type,
        selectedCampaignIds,
        triggerId,
        sendConditions,
        publishAttempt: saveAttempt > 0,
        draftAttempt: hasTriedSave,
        dayOptionalByRow,
      }),
    [
      channel,
      saveAttempt,
      templateName,
      subjects,
      type,
      selectedCampaignIds,
      triggerId,
      sendConditions,
      hasTriedSave,
      dayOptionalByRow,
    ],
  );

  useEffect(() => {
    onFormChange?.({
      templateName,
      type,
      language,
      subjects,
      selectedCampaignIds,
      selectedTagIds,
      triggerId,
      sendConditions,
      selectedTenantIds,
      isForAllTenants,
    });
  }, [
    templateName,
    type,
    language,
    subjects,
    selectedCampaignIds,
    selectedTagIds,
    triggerId,
    sendConditions,
    selectedTenantIds,
    isForAllTenants,
    onFormChange,
  ]);

  // Report the touched state upward so button-disable logic can mirror what the user
  // actually sees (display-only gating; the submit-time check still validates everything).
  useEffect(() => {
    onTouchedChange?.({
      touched: touchedFields.scalar,
      subjectsTouched: touchedFields.subjects,
      sendConditionsTouched: touchedFields.sendConditions,
    });
  }, [touchedFields.scalar, touchedFields.subjects, touchedFields.sendConditions, onTouchedChange]);

  const templateNameRequiredErrorRaw =
    errors.templateNameRequiredError ||
    (requireTemplateName && String(templateName ?? '').trim().length === 0 ? ERROR_REQUIRED : '');

  const templateNameRequiredError = touchedFields.scalar.templateName
    ? templateNameRequiredErrorRaw
    : '';

  const templateNameInvalid = Boolean(errors.templateNameMaxError || templateNameRequiredError);

  // Required errors are gated by "touched"; the max-length error always shows (it can only
  // exist once the user has typed). subjectsError / sendConditionsRequiredError stay only
  // as blocking flags and are not displayed.
  const displayErrors = {
    ...errors,
    campaignError: touchedFields.scalar.campaign ? errors.campaignError : '',
    triggerError: touchedFields.scalar.trigger ? errors.triggerError : '',
  };

  const canAddNextSubject = useMemo(
    () => canAddSubject({ subjects, isEmail }),
    [subjects, isEmail],
  );

  const subjectErrors = useMemo(
    () =>
      buildSubjectErrors({ subjects, isEmail, saveAttempt }).map((error, index) => ({
        requiredError: touchedFields.subjects[index] ? error.requiredError : '',
        maxLenError: error.maxLenError,
      })),
    [subjects, isEmail, saveAttempt, touchedFields.subjects],
  );

  const languageOptions = useMemo(
    () =>
      buildLanguageOptions({
        additionalLanguages: tenantAdditionalLanguages,
        currentLanguage: language,
      }),
    [tenantAdditionalLanguages, language],
  );

  const canEditLanguage = isLanguageEditable({ isEditMode, currentStatus });
  const showLanguageSelector =
    canEditLanguage && tenantHasLanguageChoice(tenantAdditionalLanguages);
  // A live template's language is locked, so it is shown as a flag in the card header
  // instead of the selector.
  const showLanguageFlag = isEditMode && isLiveTemplateStatus(currentStatus);

  const campaignOptions = useMemo(() => mapNamedIdOptions(campaigns), [campaigns]);
  const tagOptions = useMemo(() => mapNamedIdOptions(tags), [tags]);
  const triggerOptions = useMemo(() => mapNamedIdOptions(triggers), [triggers]);
  const tenantOptions = useMemo(
    () => [{ value: SELECT_ALL_OPTION_ID, label: 'Select all' }, ...mapNamedIdOptions(tenants)],
    [tenants],
  );

  const onChangeTenants = (next) => {
    const nextIds = (next ?? []).map(String).filter((id) => id !== SELECT_ALL_OPTION_ID);
    setSelectedTenantIds(nextIds);
  };

  // Create-only required error, gated by touched like the other scalar fields: revealed on a
  // save/publish attempt and cleared live once a tenant is picked.
  const tenantRequiredErrorRaw =
    !isEditMode && isAdmin && selectedTenantIds.length === 0 ? ERROR_REQUIRED : '';
  const tenantRequiredError = touchedFields.scalar.tenant ? tenantRequiredErrorRaw : '';

  const { rowErrors: triggerBackendRowErrors, validateNow: onSendConditionBlur } =
    useTriggerValidation({ type, triggerId, sendConditions, templateId, dayOptionalByRow });

  const sendConditionsRequired = useMemo(
    () =>
      isTriggerSectionRequired({
        isTrigger: !isMarketing,
        triggerId,
        sendConditions,
        publishAttempt: saveAttempt > 0,
        draftAttempt: hasTriedSave,
      }),
    [isMarketing, triggerId, sendConditions, saveAttempt, hasTriedSave],
  );

  const sendConditionRowErrors = useMemo(
    () =>
      buildSendConditionRowErrors({
        sendConditions,
        requiredByRow: sendConditions.map((_, index) => ({
          sendCondition:
            sendConditionsRequired && Boolean(touchedFields.sendConditions[index]?.sendCondition),
          // No required error for rows whose Days may be empty (admins / untouched migrated rows).
          day:
            !dayOptionalByRow[index] &&
            sendConditionsRequired &&
            Boolean(touchedFields.sendConditions[index]?.day),
        })),
        backendRowErrors: triggerBackendRowErrors,
        lastEditedIndex: lastEditedSendConditionIndex,
      }),
    [
      sendConditions,
      sendConditionsRequired,
      touchedFields.sendConditions,
      triggerBackendRowErrors,
      lastEditedSendConditionIndex,
      dayOptionalByRow,
    ],
  );

  const onSetSubjectAt = (index, nextValue) => {
    setSubjects((prev) => setSubjectAt({ subjects: prev, index, nextValue }));
  };

  const onRemoveSubjectAt = (index) => {
    setSubjects((prev) => removeSubjectAt({ subjects: prev, index }));
    touchedFields.removeSubject(index);
  };

  const onAddSubject = () => {
    if (!canAddNextSubject) return;
    setSubjects((prev) => addSubject({ subjects: prev }));
    touchedFields.addSubject();
  };

  const canAddNextSendCondition = useMemo(
    () =>
      canAddSendCondition(sendConditions, {
        lastRowDayOptional: dayOptionalByRow[dayOptionalByRow.length - 1] ?? false,
      }),
    [sendConditions, dayOptionalByRow],
  );

  const onChangeSendConditionAt = (index, patch) => {
    setLastEditedSendConditionIndex(index);
    setSendConditions((prev) => setSendConditionAt({ items: prev, index, patch }));
  };

  const onRemoveSendConditionAt = (index) => {
    setLastEditedSendConditionIndex(-1);
    setSendConditions((prev) => removeSendConditionAt({ items: prev, index }));
    touchedFields.removeSendCondition(index);
  };

  const onAddSendCondition = () => {
    if (!canAddNextSendCondition) return;
    setSendConditions((prev) => addSendCondition({ items: prev }));
    touchedFields.addSendCondition();
  };

  const onTypeChange = (nextType) => {
    setType(nextType);
    if (nextType === TEMPLATE_TYPE.MARKETING) {
      setTriggerId('');
      setSendConditions([makeSendCondition()]);
      touchedFields.resetTriggerSection();
    }
  };

  return {
    templateName,
    type,
    language,
    languageOptions,
    showLanguageSelector,
    showLanguageFlag,
    onLanguageChange: setLanguage,
    subjects,
    selectedCampaignIds,
    setSelectedCampaignIds,
    selectedTagIds,
    setSelectedTagIds,
    triggerId,
    setTriggerId,
    sendConditions,
    onChangeSendConditionAt,
    onRemoveSendConditionAt,
    onAddSendCondition,
    canAddNextSendCondition,
    onSendConditionBlur,
    sendConditionRowErrors,
    campaignWidthProbeRef,
    tagsMenuWidthPx,
    isEmail,
    isMarketing,
    errors: displayErrors,
    templateNameRequiredError,
    templateNameInvalid,
    canAddNextSubject,
    subjectErrors,
    campaignOptions,
    tagOptions,
    triggerOptions,
    tenantOptions,
    selectAllTenantValue: SELECT_ALL_OPTION_ID,
    selectedTenantIds,
    onChangeTenants,
    tenantRequiredError,
    onSetSubjectAt,
    onRemoveSubjectAt,
    onAddSubject,
    onTypeChange,
    setTemplateName,
  };
}
